import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImportDialog } from "@/components/ImportDialog";
import { ListActions } from "@/components/ListActions";
import { Plus, Pencil, Building2 } from "lucide-react";

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#A855F7", "#EF4444", "#06B6D4", "#F97316"];

export default function Sectors() {
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", color: "#10B981" });

  const load = () => api.get("/sectors").then((r) => setSectors(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", color: "#10B981" });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description, color: s.color });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome é obrigatório");
    try {
      if (editing) {
        await api.put(`/sectors/${editing.id}`, form);
        toast.success("Setor atualizado");
      } else {
        await api.post("/sectors", form);
        toast.success("Setor criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Erro ao salvar");
    }
  };

  const toggleActive = (s, active) => api.put(`/sectors/${s.id}`, { name: s.name, description: s.description || "", color: s.color, active });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Setores
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Organize suas OS por setor/departamento.</p>
        </div>
        <ImportDialog resource="sectors" label="setor" onDone={load} />
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-sector-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo setor
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sectors.map((s, i) => (
          <Card key={s.id} className={`p-5 border-slate-200 dark:border-slate-800 hover:shadow-md transition-all ${s.active === false ? "opacity-60" : ""}`} data-testid={`sector-card-${s.name}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${s.color}20` }}>
                  <Building2 className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">{s.name}{s.active === false && <span className="ml-2 text-[10px] uppercase text-slate-400 font-bold">Inativo</span>}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{s.description || "—"}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4 items-center flex-wrap">
              <Button variant="outline" size="sm" onClick={() => openEdit(s)} className="flex-1" data-testid={`edit-sector-${s.name}`}>
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              <ListActions item={s} index={i} rows={sectors} resource="sectors" isActive={s.active !== false} onToggle={toggleActive} onChanged={load} testKey={`sector-${s.name}`} />
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar setor" : "Novo setor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input data-testid="sector-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input data-testid="sector-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Cor</Label>
              <div className="flex gap-2 mt-1.5">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full border-2 ${form.color === c ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-sector-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
